import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { EndpointsService } from '../api/endpoints.service';
import { Order } from 'src/app/interfaces/orders';
import { CoreService } from './core.service';

const POLLING_FALLBACK_INTERVAL_MS = 60000;

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  connectionStatus = new BehaviorSubject<'connected' | 'connecting' | 'disconnected'>('disconnected');

  private socket: Socket | null = null;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private endpoints: EndpointsService,
    private injector: Injector,
  ) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.socket && !this.socket.connected) {
        this.socket.connect();
      }
    });
  }

  // Lazy statt Constructor-Injection, um zyklische DI mit CoreService -> LoginService -> SocketService zu vermeiden
  private get core(): CoreService {
    return this.injector.get(CoreService);
  }

  connect(token: string): void {
    if (this.socket?.connected) {
      return;
    }

    this.connectionStatus.next('connecting');

    this.socket = io(this.endpoints.kontorUrl, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      this.connectionStatus.next('connected');
      this.stopPollingFallback();
    });

    this.socket.on('disconnect', () => {
      this.connectionStatus.next('disconnected');
      this.startPollingFallback();
    });

    this.socket.on('connect_error', (err: Error) => {
      console.error('Socket connect error:', err);
      this.connectionStatus.next('disconnected');
      this.startPollingFallback();
    });

    this.socket.on('order:created', (order: Order) => {
      this.core.handleOrderCreatedEvent(order);
    });

    this.socket.on('order:updated', (order: Order) => {
      this.core.handleOrderUpdatedEvent(order);
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connectionStatus.next('disconnected');
    this.stopPollingFallback();
  }

  private startPollingFallback(): void {
    if (this.pollingTimer) {
      return;
    }
    this.pollingTimer = setInterval(() => this.core.refreshOrders(), POLLING_FALLBACK_INTERVAL_MS);
  }

  private stopPollingFallback(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}
